export type CompressorCalculationFamily = 'semi-hermetico' | 'hermetico';

export type StandardConditions = {
  condensingTemperatureC?: number;
  suctionTemperatureC: number;
  liquidTemperatureC?: number;
  subcoolingK: number;
};

export type CompressorCalculationModel = {
  family: CompressorCalculationFamily;
  name: string;
  standardName: string;
  volumetricCalibrationExponent: (pressureRatio: number) => number;
  isentropicPressureRatio: (suctionPressure: number, dischargePressure: number) => number;
  currentDenominator: (voltage: number, phase?: string) => number;
  standardConditions: (evaporationTemperatureC: number, application?: string) => StandardConditions;
  standardDisplayName: (application?: string) => string;
};

const semiHermeticModel: CompressorCalculationModel = {
  family: 'semi-hermetico',
  name: 'Semi-Hermético',
  standardName: 'EN12900',
  volumetricCalibrationExponent: () => 0.5,
  isentropicPressureRatio: (suctionPressure, dischargePressure) => dischargePressure / suctionPressure,
  currentDenominator: (voltage) => Math.sqrt(3) * voltage,
  standardConditions: () => ({
    suctionTemperatureC: 20,
    subcoolingK: 0,
  }),
  standardDisplayName: () => 'EN12900',
};

const hermeticModel: CompressorCalculationModel = {
  family: 'hermetico',
  name: 'Hermético',
  standardName: 'ASHRAE',
  volumetricCalibrationExponent: (pressureRatio) => Math.max(0.1, 0.5 - 0.02 * pressureRatio),
  isentropicPressureRatio: (suctionPressure, dischargePressure) =>
    (0.05 * dischargePressure) / suctionPressure,
  currentDenominator: (voltage, phase) =>
    String(phase || '3F').trim().toUpperCase() === '1F' ? voltage : Math.sqrt(3) * voltage,
  standardConditions: (_evaporationTemperatureC, application) => {
    const isLowBackPressure = String(application || '').toUpperCase().includes('LBP');
    const condensingTemperatureC = 54.4;
    const suctionTemperatureC = isLowBackPressure ? 32.2 : 35;
    const liquidTemperatureC = isLowBackPressure ? 32.2 : 46.1;

    return {
      condensingTemperatureC,
      suctionTemperatureC,
      liquidTemperatureC,
      subcoolingK: condensingTemperatureC - liquidTemperatureC,
    };
  },
  standardDisplayName: (application) =>
    `ASHRAE (${String(application || '').toUpperCase().includes('LBP') ? 'LBP' : 'MBP'})`,
};

const calculationModels: Record<CompressorCalculationFamily, CompressorCalculationModel> = {
  'semi-hermetico': semiHermeticModel,
  hermetico: hermeticModel,
};

export const getCompressorCalculationModel = (
  family: CompressorCalculationFamily
): CompressorCalculationModel => calculationModels[family];

